const HELP_DOCUMENT_VARIANTS = {
  short: '',
  full: '.full',
};

const FULL_HELP_DOCUMENT_SLUGS = new Set([
  'commission-plans',
]);

const documentCache = new Map();

function normalizeLocale(locale) {
  const value = String(locale || 'en').trim();
  return value || 'en';
}

function normalizeSlug(slug) {
  return String(slug || '').trim().replace(/^\/+|\/+$/g, '');
}

function getVariantSuffix(variant) {
  return HELP_DOCUMENT_VARIANTS[variant] ?? HELP_DOCUMENT_VARIANTS.short;
}

async function fetchMarkdownDocument(path) {
  const response = await fetch(path);
  if (!response.ok) {
    throw new Error('not found');
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/html')) {
    throw new Error('not found');
  }

  return response.text();
}

function createHelpDocumentResult(markdown, isFallback) {
  return {
    markdown,
    isFallback,
    hasContent: Boolean(markdown),
  };
}

export function getHelpDocumentPath({ locale, slug, variant = 'short' }) {
  const normalizedLocale = normalizeLocale(locale);
  const normalizedSlug = normalizeSlug(slug);
  const suffix = getVariantSuffix(variant);

  return `/help/${normalizedLocale}/${normalizedSlug}${suffix}.md`;
}

export function hasFullHelpDocument(slug) {
  return FULL_HELP_DOCUMENT_SLUGS.has(normalizeSlug(slug));
}

export async function loadHelpDocument({ locale, slug, variant = 'short' }) {
  const normalizedLocale = normalizeLocale(locale);
  const normalizedSlug = normalizeSlug(slug);

  if (!normalizedSlug) {
    return createHelpDocumentResult('', false);
  }

  const key = `${normalizedLocale}:${variant}:${normalizedSlug}`;
  if (documentCache.has(key)) {
    return documentCache.get(key);
  }

  try {
    const markdown = await fetchMarkdownDocument(
      getHelpDocumentPath({ locale: normalizedLocale, slug: normalizedSlug, variant }),
    );
    const result = createHelpDocumentResult(markdown, false);
    documentCache.set(key, result);
    return result;
  } catch {
    if (normalizedLocale === 'en') {
      const result = createHelpDocumentResult('', false);
      documentCache.set(key, result);
      return result;
    }

    const fallbackKey = `en:${variant}:${normalizedSlug}`;
    if (documentCache.has(fallbackKey)) {
      const fallback = documentCache.get(fallbackKey);
      const result = createHelpDocumentResult(fallback.markdown, Boolean(fallback.markdown));
      documentCache.set(key, result);
      return result;
    }

    try {
      const markdown = await fetchMarkdownDocument(
        getHelpDocumentPath({ locale: 'en', slug: normalizedSlug, variant }),
      );
      const fallback = createHelpDocumentResult(markdown, false);
      const result = createHelpDocumentResult(markdown, Boolean(markdown));
      documentCache.set(fallbackKey, fallback);
      documentCache.set(key, result);
      return result;
    } catch {
      const result = createHelpDocumentResult('', false);
      documentCache.set(fallbackKey, result);
      documentCache.set(key, result);
      return result;
    }
  }
}
